import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getUrlParams } from '@src/common/getUrlParams';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { buyerSelector } from '@src/features/customers';
import { setupCheckout } from '@src/features/orders';
import { directLinkStateSelector } from '@src/stores/directLink';
import { modeToPath, pathToMode } from '@src/views/checkout/routing';
import { CheckoutMode } from '../types';
import { getInitialCheckoutMode } from '../utils';
import { useCheckoutBootstrap } from './useCheckoutBootstrap';
import { useCheckoutHostCallbacks } from './useCheckoutHostCallbacks';
import { useCheckoutModeDecision } from './useCheckoutModeDecision';

type UseCheckoutFlowParams = {
  editable?: boolean;
  skipResumeCart?: boolean;
  skipConfirmation?: boolean;
};

type UseCheckoutFlowResult = {
  mode: CheckoutMode;
  goTo: (mode: CheckoutMode) => void;
  editable: boolean;
  skipResumeCart: boolean;
  skipConfirmation: boolean;
  /** The step is not settled yet — the layout shows a placeholder instead of `mode`. */
  bootstrapping: boolean;
};

const useCheckoutFlowShared = (params: UseCheckoutFlowParams = {}) => {
  const { editable = true, skipResumeCart = false, skipConfirmation = false } = params;

  const dispatch = useAppDispatch();
  const [buyer] = useAppSelector(buyerSelector);
  const skipResumeCartPage = useWidgetParam('skip-resume-cart-page');
  const { directLink } = useAppSelector(directLinkStateSelector);

  const initialMode = getInitialCheckoutMode({ skipResumeCart, skipResumeCartPage, directLink });

  useEffect(() => {
    const orderId = getUrlParams('orderId');

    if (orderId && buyer) {
      dispatch(setupCheckout(orderId));
    }
  }, [buyer, dispatch]);

  return {
    initialMode,
    editable,
    skipResumeCart,
    skipConfirmation,
    onCompleted: useCheckoutHostCallbacks(),
  };
};

/** Mode-state navigation (default when `data-widget-use-routing` is unset/invalid). */
export const useCheckoutFlow = (params: UseCheckoutFlowParams = {}): UseCheckoutFlowResult => {
  const shared = useCheckoutFlowShared(params);
  const [mode, setMode] = useState<CheckoutMode>(shared.initialMode);

  const goTo = useCallback((next: CheckoutMode) => {
    setMode(next);
  }, []);

  const { bootstrapping } = useCheckoutBootstrap({
    mode,
    initialMode: shared.initialMode,
    skipConfirmation: shared.skipConfirmation,
    redirectTo: goTo,
  });

  useCheckoutModeDecision({
    mode,
    redirectTo: goTo,
    skipConfirmation: shared.skipConfirmation,
    onCompleted: shared.onCompleted,
    bootstrapping,
  });

  return {
    mode,
    goTo,
    editable: shared.editable,
    skipResumeCart: shared.skipResumeCart,
    skipConfirmation: shared.skipConfirmation,
    bootstrapping,
  };
};

/** Path-based navigation — must run under MemoryRouter or HashRouter. */
export const useRoutedCheckoutFlow = (params: UseCheckoutFlowParams = {}): UseCheckoutFlowResult => {
  const shared = useCheckoutFlowShared(params);
  const location = useLocation();
  const navigate = useNavigate();

  const mode = pathToMode(location.pathname) ?? shared.initialMode;

  const goTo = useCallback(
    (next: CheckoutMode) => {
      navigate(modeToPath(next));
    },
    [navigate],
  );

  const redirectTo = useCallback(
    (next: CheckoutMode) => {
      navigate(modeToPath(next), { replace: true });
    },
    [navigate],
  );

  const { bootstrapping } = useCheckoutBootstrap({
    mode,
    initialMode: shared.initialMode,
    skipConfirmation: shared.skipConfirmation,
    redirectTo,
  });

  useCheckoutModeDecision({
    mode,
    redirectTo,
    skipConfirmation: shared.skipConfirmation,
    onCompleted: shared.onCompleted,
    bootstrapping,
  });

  return {
    mode,
    goTo,
    editable: shared.editable,
    skipResumeCart: shared.skipResumeCart,
    skipConfirmation: shared.skipConfirmation,
    bootstrapping,
  };
};

export default useCheckoutFlow;
